import { ColumnDef } from '@tanstack/react-table';
import { ColumnMeta, FilterResultMethod } from '@toggle/toggle';
import React from 'react';

import { OneDayReturnCell } from '~/components/one-day-return-cell/OneDayReturnCell';
import { PriceCellTooltip } from '~/components/price-cell/price-cell-tooltip/PriceCellTooltip';
import { PriceCell } from '~/components/price-cell/PriceCell';
import { LIVE_ONE_DAY_RETURN_COLUMN_META_VALUE_FIELD } from '~/views/screener/results/constants';
import {
  ColumnMetaWithKey,
  LIVE_PRICE_COLUMN_META_VALUE_FIELD,
  sortingFn,
} from '~/views/screener/results/screener-table-utils';
import { ScreenerCell } from '~/views/screener/screener-cell/ScreenerCell';
import {
  LIVE_RETURN_1D_FIELD,
  PREV_RETURN_1D_FIELD,
} from '~/views/watchlist-page/hooks/useWatchlistTable';

import { EntityPlusPrice } from './useAssetsTableData';

type ScenarioInsightsType = 'Fundamental' | 'Technical' | 'Macroeconomics';
type KGInsightsType = 'earnings' | 'company';
export type InsightsAnalysisType = ScenarioInsightsType | KGInsightsType;

export const getInsightsAnalysis = ({
  isCompanyCatalystEnabled,
  isEarningsInsightsEnabled,
}: {
  isCompanyCatalystEnabled: boolean;
  isEarningsInsightsEnabled: boolean;
}): Record<string, InsightsAnalysisType[]> => ({
  kg: [
    ...(isEarningsInsightsEnabled ? (['earnings'] as const) : []),
    ...(isCompanyCatalystEnabled ? (['company'] as const) : []),
  ],
  scenario: ['Fundamental', 'Technical', 'Macroeconomics'],
});

export const getDefaultInsightsFilterState = (
  selected: boolean,
  featFlags: {
    isCompanyCatalystEnabled: boolean;
    isEarningsInsightsEnabled: boolean;
  } = {
    isCompanyCatalystEnabled: true,
    isEarningsInsightsEnabled: true,
  }
) =>
  Object.fromEntries(
    Object.entries(getInsightsAnalysis(featFlags)).map(([key, val]) => [
      key,
      val.map(v => ({ key: v, checked: selected })),
    ])
  );

export const mapColumnMeta = (
  c: ColumnMetaWithKey | ColumnMeta,
  isDisabled?: boolean
): ColumnDef<EntityPlusPrice> => {
  const isPriceCell = c.field === LIVE_PRICE_COLUMN_META_VALUE_FIELD;
  const isOneDayReturnCell =
    c.field === LIVE_ONE_DAY_RETURN_COLUMN_META_VALUE_FIELD ||
    c.field === LIVE_RETURN_1D_FIELD;
  const isPrevOneDayReturnCell = c.field === PREV_RETURN_1D_FIELD;

  return {
    id: c.field,
    accessorFn: row => row[c.field],
    header: c.name,
    cell: ({ getValue, row }) => {
      const rowData = row.original;

      if (isPriceCell) {
        return <PriceCell asset={rowData.asset} />;
      } else if (isOneDayReturnCell) {
        return (
          <OneDayReturnCell asset={rowData.asset} isDisabled={isDisabled} />
        );
      } else {
        const value = getValue<FilterResultMethod>();
        const tooltipValue = value.tooltip;
        const customPrevOneDayTooltip = tooltipValue ? (
          <PriceCellTooltip date={tooltipValue} type={'notSupported'} />
        ) : undefined;

        return (
          <ScreenerCell
            value={value}
            columnMeta={c}
            isDisabled={isDisabled}
            customTooltip={
              isPrevOneDayReturnCell ? customPrevOneDayTooltip : undefined
            }
          />
        );
      }
    },
    meta: c,
    sortingFn,
  };
};
